import { useCallback, useEffect, useId, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, extractErrorMessage } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { HelpTip } from '../components/HelpTip';
import { VerInstructivo } from '../components/VerInstructivo';
import { Obligatorio, LeyendaObligatorio } from '../components/Obligatorio';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { useCamposObligatorios } from '../hooks/useCamposObligatorios';
import { ETIQUETAS_STOCK, formatoStock } from '../components/StockSku';
import { CodigoArticulo } from '../components/CodigoArticulo';
import {
  DetalleConteoModal, ESTADO_CONTEO, TIPO_CONTEO, URL_CONTEOS, fechaCorta, type EstadoConteo, type TipoConteo,
} from './conteos-inventario/DetalleConteo';
import { MAX_DIAS_ATRAS_HECHO, ahoraLocalAr, fechaHoraAr, localArAIso, minHechoLocalAr } from '../lib/fechaHoraAr';
import { cargarIdentidades, etiquetaArticulo } from '../lib/identidadArticulos';

/**
 * Conteos de inventario (etapa 1): la carga del stock inicial y los conteos cíclicos por SKU. Un conteo se carga en
 * borrador (a mano o desde una planilla) y lo confirma el responsable: ajusta el depósito con la diferencia contra el
 * stock que había a la hora del conteo ("Contado el", o la hora de carga de cada línea) y realinea el stock para vender;
 * lo que salió o entró después sigue aplicado. Ver: wms:ver; cargar: wms:editar; confirmar: admin.
 * Es el ÚNICO lugar para contar stock (DECISIONES 2026-10-02): arriba avisa los SKU pausados por el stock de resguardo
 * ("Contar estos SKU" arma un conteo cíclico con ellos por contar) y "Contar este SKU" de la vista previa de Sincronizar con ML llega
 * con ?sku= y abre el alta con ese SKU. Confirmar un conteo con unidades libera el stock de resguardo (backend).
 */

interface FilaConteo {
  id: number;
  fecha: string;
  tipo: TipoConteo;
  ubicacionCodigo: string | null;
  ciego: boolean;
  estado: EstadoConteo;
  notas: string | null;
  contadoEl?: string | null;
  lineas: number;
  creadoPor: string | null;
  createdAt: string;
  confirmadoPor: string | null;
  confirmadoEn: string | null;
}

/**
 * SKU que sincroniza con ML y al que sólo le queda el stock de resguardo (GET conteos-inventario/pausados). Los números
 * llegan sólo a quien ve el sistema en un conteo ciego (el responsable); a los demás, SKU y descripción.
 */
interface SkuPausado {
  sku: string;
  descripcion: string | null;
  fisico?: number;
  reservado?: number;
  disponible?: number;
  /** Stock de resguardo configurado. */
  reserva?: number;
}

const URL_PAUSADOS = `${URL_CONTEOS}/pausados`;
const TITULO_PAUSADOS = 'SKU pausados: contar para volver a publicar';
const NOTAS_CONTEO_PAUSADOS = 'SKU pausados por stock de resguardo';

/** Hoy en Argentina (AAAA-MM-DD) para el campo de fecha. */
const hoyAR = () => new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString().slice(0, 10);
const formVacio = () => ({ tipo: '' as TipoConteo | '', ciego: false, ciegoElegido: false, fecha: hoyAR(), contadoEl: '', notas: '' });
/** Alta para contar un SKU (viene de "Contar este SKU"): cíclico, con el ciego por defecto del cíclico. */
const formParaSku = (sku: string) => ({ ...formVacio(), tipo: 'ciclico' as TipoConteo | '', ciego: true, notas: `Conteo de ${sku}` });

/** Aviso de los SKU pausados por el stock de resguardo; no se muestra si no hay ninguno. */
function AvisoPausados({ pausados, error, puedeCargar, creando, onContar }: {
  pausados: SkuPausado[]; error: string; puedeCargar: boolean; creando: boolean; onContar: () => void;
}) {
  const idTitulo = useId();
  if (error) return <p className="text-sm text-amber-800">No se pudieron leer los SKU pausados: {error}</p>;
  if (pausados.length === 0) return null;
  // Sin los números (quien sólo carga): sólo SKU y descripción, así el aviso no delata el sistema de un conteo ciego.
  const conStock = pausados.some((p) => typeof p.fisico === 'number');
  return (
    <section aria-labelledby={idTitulo} className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={idTitulo} className="text-base font-semibold text-amber-900">{TITULO_PAUSADOS}</h2>
          <p className="text-sm text-amber-900">
            Sincronizan con Mercado Libre y sólo les queda el stock de resguardo, así que sus publicaciones están pausadas.
            Al confirmar un conteo con unidades, el stock de resguardo se libera y se vuelven a publicar.
          </p>
        </div>
        <Button
          onClick={onContar}
          loading={creando}
          disabled={!puedeCargar}
          title={!puedeCargar ? 'No tenés permiso para realizar esta acción' : undefined}
        >
          Contar estos SKU
        </Button>
      </div>
      <div className="overflow-x-auto bg-white rounded border border-amber-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-200">
              <th className="py-2 px-3 font-medium">SKU</th>
              <th className="py-2 px-3 font-medium">Descripción</th>
              {conStock && (
                <>
                  <th className="py-2 px-3 font-medium text-right">{ETIQUETAS_STOCK.fisico}</th>
                  <th className="py-2 px-3 font-medium text-right">{ETIQUETAS_STOCK.reservado}</th>
                  <th className="py-2 px-3 font-medium text-right">{ETIQUETAS_STOCK.disponible}</th>
                  <th className="py-2 px-3 font-medium text-right">Stock de resguardo</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {pausados.map((p) => (
              <tr key={p.sku} className="border-b border-slate-100">
                <td className="py-1 px-3"><CodigoArticulo sku={p.sku} /></td>
                <td className="py-1 px-3">{p.descripcion ?? '—'}</td>
                {conStock && (
                  <>
                    <td className="py-1 px-3 text-right">{formatoStock(p.fisico)}</td>
                    <td className="py-1 px-3 text-right">{formatoStock(p.reservado)}</td>
                    <td className="py-1 px-3 text-right">{formatoStock(p.disponible)}</td>
                    <td className="py-1 px-3 text-right">{formatoStock(p.reserva)}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Se monta al abrirse (el formulario arranca vacío cada vez y la foto de "sin cambios" es la del formulario inicial).
 * Con `sku` (de "Contar este SKU") arranca cíclico y con el SKU anotado; al crearlo, queda por contar en el detalle.
 */
function NuevoConteoModal({ open, sku, onClose, onCreado }: {
  open: boolean; sku?: string | null; onClose: () => void; onCreado: (id: number) => void;
}) {
  const { addToast } = useToast();
  const [form, setForm] = useState(() => (sku ? formParaSku(sku) : formVacio()));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const cierre = useCierreConfirmado(useHayCambios(form, open), onClose);
  const obligatorios = useCamposObligatorios(open);

  const elegirTipo = (tipo: TipoConteo | '') => setForm((f) => ({
    ...f, tipo,
    // Cíclico: ciego por defecto (el que cuenta no se deja llevar por el sistema); inicial: no. Si ya lo eligió, se respeta.
    ciego: f.ciegoElegido ? f.ciego : tipo === 'ciclico',
  }));

  const crear = async () => {
    if (guardando || !obligatorios.validar()) return;
    const contadoEl = form.contadoEl ? localArAIso(form.contadoEl) : null;
    setGuardando(true);
    setError('');
    try {
      const creado = await api.post<{ id: number }>(URL_CONTEOS, {
        tipo: form.tipo,
        ciego: form.ciego,
        ...(form.fecha ? { fecha: form.fecha } : {}),
        ...(contadoEl ? { contadoEl } : {}),
        ...(form.notas.trim() ? { notas: form.notas.trim() } : {}),
      });
      addToast(`Conteo #${creado.id} creado: cargá lo contado`, 'success');
      onCreado(creado.id);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal open={open} cierre={cierre} title="Nuevo conteo de inventario" size="sm">
      <div {...obligatorios.contenedor} className="space-y-4">
        <LeyendaObligatorio />
        {sku && (
          <p className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            Se va a contar <strong className="font-mono">{sku}</strong>: al crear el conteo queda en la lista "Por contar".
          </p>
        )}
        <div>
          <label htmlFor="conteo-tipo" className="block text-sm font-medium mb-1">Tipo<Obligatorio /></label>
          <select
            id="conteo-tipo"
            aria-label="Tipo"
            required
            className="w-full px-3 py-2 border rounded-lg"
            value={form.tipo}
            onChange={(e) => elegirTipo(e.target.value as TipoConteo | '')}
          >
            <option value="">Elegí el tipo</option>
            <option value="inicial">{TIPO_CONTEO.inicial} (carga del stock de arranque)</option>
            <option value="ciclico">{TIPO_CONTEO.ciclico} (control periódico por SKU)</option>
          </select>
        </div>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={form.ciego}
            onChange={(e) => setForm((f) => ({ ...f, ciego: e.target.checked, ciegoElegido: true }))}
          />
          <span>
            Conteo ciego
            <span className="block text-xs text-slate-500">Quien carga no ve lo que dice el sistema hasta que se confirma; el responsable sí.</span>
          </span>
        </label>
        <div>
          <label htmlFor="conteo-fecha" className="block text-sm font-medium mb-1">Fecha del conteo</label>
          <input id="conteo-fecha" type="date" className="w-full px-3 py-2 border rounded-lg" value={form.fecha}
            onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))} />
        </div>
        <div>
          <label htmlFor="conteo-contado-el" className="block text-sm font-medium mb-1">Contado el</label>
          <input id="conteo-contado-el" type="datetime-local" aria-label="Contado el" className="w-full px-3 py-2 border rounded-lg"
            value={form.contadoEl} min={minHechoLocalAr()} max={ahoraLocalAr()}
            onChange={(e) => setForm((f) => ({ ...f, contadoEl: e.target.value }))} />
          <p className="text-xs text-slate-500 mt-1">
            Si contaste antes de cargarlo (por ejemplo, en papel), poné la fecha y hora en que contaste (hora de Argentina, hasta {MAX_DIAS_ATRAS_HECHO} días atrás).
            Vacío: cada línea toma la hora en que se carga.
          </p>
        </div>
        <div>
          <label htmlFor="conteo-notas" className="block text-sm font-medium mb-1">Notas</label>
          <textarea id="conteo-notas" aria-label="Notas" rows={2} maxLength={500} className="w-full px-3 py-2 border rounded-lg"
            placeholder="Ej.: pasillo 1, estantes de arriba" value={form.notas} onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))} />
        </div>
        <p className="text-xs text-slate-500">Se cuenta en el depósito general (hoy es la única ubicación).</p>
        {error && <p className="text-red-600 text-sm">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={cierre.cerrar} className="px-4 py-2 text-sm">Cancelar</button>
          <Button onClick={() => void crear()} loading={guardando}>Crear conteo</Button>
        </div>
      </div>
    </Modal>
  );
}

export function ConteosInventarioPage() {
  const { can, user } = useAuth();
  const { addToast } = useToast();
  const puedeCargar = can('wms', 'editar');
  const esResponsable = user?.role === 'admin' && puedeCargar;
  const [estado, setEstado] = useState('');
  const [tipo, setTipo] = useState('');
  const [lista, setLista] = useState<FilaConteo[]>([]);
  const [cargando, setCargando] = useState(false);
  const [nuevoAbierto, setNuevoAbierto] = useState(false);
  const [abierto, setAbierto] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  /** SKU que llegó con ?sku= ("Contar este SKU"): el alta lo anota. */
  const [skuPedido, setSkuPedido] = useState<string | null>(null);
  /** SKU sugeridos para contar por conteo (los pausados, o el de "Contar este SKU"), mientras la página está abierta. */
  const [porContarDe, setPorContarDe] = useState<Record<number, string[]>>({});
  const [pausados, setPausados] = useState<SkuPausado[]>([]);
  const [errorPausados, setErrorPausados] = useState('');
  const [creandoPausados, setCreandoPausados] = useState(false);

  const cargar = useCallback(async () => {
    const params = new URLSearchParams();
    if (estado) params.set('estado', estado);
    if (tipo) params.set('tipo', tipo);
    const q = params.toString();
    setCargando(true);
    try {
      setLista(await api.get<FilaConteo[]>(`${URL_CONTEOS}${q ? `?${q}` : ''}`));
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
    } finally {
      setCargando(false);
    }
  }, [estado, tipo, addToast]);

  useEffect(() => { void cargar(); }, [cargar]);

  const cargarPausados = useCallback(async () => {
    try {
      const filas = await api.get<SkuPausado[]>(URL_PAUSADOS);
      setPausados(filas ?? []);
      setErrorPausados('');
    } catch (e) {
      setErrorPausados(extractErrorMessage(e));
    }
  }, []);

  useEffect(() => { void cargarPausados(); }, [cargarPausados]);

  // "Contar este SKU" (vista previa de Sincronizar con ML): abre el alta con el SKU y saca ?sku= para que recargar no la reabra.
  const skuEnUrl = searchParams.get('sku')?.trim() ?? '';
  useEffect(() => {
    if (!skuEnUrl) return;
    if (puedeCargar) {
      setSkuPedido(skuEnUrl);
      setNuevoAbierto(true);
    } else {
      // The link carries the internal ID: the message names the article by its code.
      const sku = skuEnUrl;
      void cargarIdentidades([sku]).then((m) => addToast(`No tenés permiso para crear un conteo de ${etiquetaArticulo(sku, m.get(sku))}`, 'error'));
    }
    setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('sku'); return n; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuEnUrl]);

  // "Conteo #N" en Movimientos de stock: abre ese conteo y saca ?conteo= para que recargar no lo reabra.
  const conteoEnUrl = Number(searchParams.get('conteo'));
  useEffect(() => {
    if (!Number.isInteger(conteoEnUrl) || conteoEnUrl <= 0) return;
    setAbierto(conteoEnUrl);
    setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('conteo'); return n; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conteoEnUrl]);

  const contarPausados = async () => {
    if (creandoPausados) return;
    setCreandoPausados(true);
    try {
      // Sin "ciego": el backend aplica el de hoy para un cíclico.
      const creado = await api.post<{ id: number }>(URL_CONTEOS, { tipo: 'ciclico', notas: NOTAS_CONTEO_PAUSADOS });
      setPorContarDe((m) => ({ ...m, [creado.id]: pausados.map((p) => p.sku) }));
      addToast(`Conteo #${creado.id} creado con ${pausados.length} SKU por contar`, 'success');
      setAbierto(creado.id);
      void cargar();
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
    } finally {
      setCreandoPausados(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 inline">Conteos de inventario</h1>
          <HelpTip
            tips={[
              'Un conteo junta lo que se contó de cada SKU en el depósito. El inicial carga el stock de arranque; los cíclicos controlan SKU cada tanto.',
              'Cargá lo contado a mano (buscando el SKU) o importá una planilla con las columnas SKU y Cantidad (y, opcional, Variante: una fila por SKU y color). Si el SKU tiene variantes, «Contar por color» carga un número por color. Los packs y combos no se cuentan: se cuentan sus componentes.',
              'El Sistema de cada línea es el stock que había en la ubicación a la hora del conteo: la de "Contado el" o, si está vacío, la hora en que se cargó la línea. Al confirmar se ajusta sólo la diferencia contra ese stock: lo que salió o entró después (despachos, facturas, ingresos) sigue descontado o sumado y no se cuenta dos veces.',
              'Si contaste en papel y lo cargás más tarde, completá "Contado el" con la hora en que contaste. En cada línea, "Movimientos desde el conteo" muestra lo que entró o salió después de esa hora.',
              'Es el único lugar para contar stock. Arriba aparecen los SKU pausados por el stock de resguardo: al contarlos y confirmar el conteo se vuelven a publicar.',
              'Manda el conteo más nuevo: si un SKU ya tiene un conteo posterior (otro conteo o un conteo de estante), su línea queda "Superada por un conteo posterior" y no se ajusta.',
              'En un conteo ciego, quien carga no ve lo que dice el sistema. Confirma el responsable (admin): ajusta el depósito, el stock para vender y Mercado Libre.',
            ]}
          />
          <VerInstructivo seccion="conteos-de-inventario" />
          <p className="text-slate-500 text-sm mt-1">Stock inicial y conteos cíclicos del depósito</p>
        </div>
        <Button
          onClick={() => setNuevoAbierto(true)}
          disabled={!puedeCargar}
          title={!puedeCargar ? 'No tenés permiso para realizar esta acción' : undefined}
        >
          + Nuevo conteo
        </Button>
      </div>

      <AvisoPausados
        pausados={pausados}
        error={errorPausados}
        puedeCargar={puedeCargar}
        creando={creandoPausados}
        onContar={() => void contarPausados()}
      />

      <div className="bg-white rounded-lg border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label htmlFor="filtro-estado" className="block text-xs font-medium text-slate-600 mb-1">Estado</label>
          <select id="filtro-estado" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="">Todos</option>
            {(Object.keys(ESTADO_CONTEO) as EstadoConteo[]).map((e) => <option key={e} value={e}>{ESTADO_CONTEO[e].etiqueta}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="filtro-tipo" className="block text-xs font-medium text-slate-600 mb-1">Tipo</label>
          <select id="filtro-tipo" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="">Todos</option>
            {(Object.keys(TIPO_CONTEO) as TipoConteo[]).map((t) => <option key={t} value={t}>{TIPO_CONTEO[t]}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-200">
              <th className="py-2 px-3 font-medium">Conteo</th>
              <th className="py-2 px-3 font-medium">Fecha</th>
              <th className="py-2 px-3 font-medium">Tipo</th>
              <th className="py-2 px-3 font-medium">Estado</th>
              <th className="py-2 px-3 font-medium text-right">Líneas</th>
              <th className="py-2 px-3 font-medium">Creado por</th>
              <th className="py-2 px-3 font-medium">Confirmado</th>
              <th className="py-2 px-3 font-medium">Notas</th>
              <th className="py-2 px-3" />
            </tr>
          </thead>
          <tbody>
            {lista.map((c) => (
              <tr key={c.id} className="border-b border-slate-100">
                <td className="py-2 px-3 font-medium">#{c.id}</td>
                <td className="py-2 px-3">{fechaCorta(c.fecha)}</td>
                <td className="py-2 px-3">
                  {TIPO_CONTEO[c.tipo]}{c.ciego && <Badge tone="slate" className="ml-1">Ciego</Badge>}
                </td>
                <td className="py-2 px-3"><Badge tone={ESTADO_CONTEO[c.estado].tono}>{ESTADO_CONTEO[c.estado].etiqueta}</Badge></td>
                <td className="py-2 px-3 text-right">{c.lineas}</td>
                <td className="py-2 px-3">{c.creadoPor ?? '—'}</td>
                <td className="py-2 px-3">{c.confirmadoEn ? `${fechaHoraAr(c.confirmadoEn)} · ${c.confirmadoPor ?? '—'}` : '—'}</td>
                <td className="py-2 px-3 text-slate-600">{c.notas ?? ''}</td>
                <td className="py-2 px-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => setAbierto(c.id)} aria-label={`Abrir conteo #${c.id}`}>Abrir</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {lista.length === 0 && (
          <p className="text-sm text-slate-500 p-4">{cargando ? 'Cargando…' : 'No hay conteos para los filtros elegidos.'}</p>
        )}
      </div>

      {nuevoAbierto && (
        <NuevoConteoModal
          open
          sku={skuPedido}
          onClose={() => { setNuevoAbierto(false); setSkuPedido(null); }}
          onCreado={(id) => {
            if (skuPedido) setPorContarDe((m) => ({ ...m, [id]: [skuPedido] }));
            setNuevoAbierto(false);
            setSkuPedido(null);
            setAbierto(id);
            void cargar();
          }}
        />
      )}
      {abierto !== null && (
        <DetalleConteoModal
          id={abierto}
          puedeCargar={puedeCargar}
          esResponsable={esResponsable}
          puedeSincronizar={user?.role === 'admin' && can('catalogo', 'editar')}
          porContar={porContarDe[abierto]}
          onClose={() => setAbierto(null)}
          onCambio={() => { void cargar(); void cargarPausados(); }}
        />
      )}
    </div>
  );
}
